import { Box, Button, Paper } from '@material-ui/core';
import { AiOutlinePlus } from 'react-icons/ai';
import { FormProvider, UseFormReturn } from 'react-hook-form';

import { useOnboardingStyles } from '@vyce/core/src/components/AppOnboarding';
import { FormDoc, FormQuestion } from '@vyce/core/src/types';
import { ButtonTitleWithLoading } from '@vyce/core/src/components/ButtonTitleWithLoading';

import { FormTitleStep } from './FormTitleStep';
import { FormQuestionStep } from './FormQuestionStep';

interface Props {
  methods: UseFormReturn<FormDoc>;
  questions: FormQuestion[];
  findQuestion: (id: string) => { question: FormQuestion; index: number };
  moveQuestion: (dragId: string, hoverIndex: number) => void;
  handleRemoveQuestion: (questionId: string) => void;
  duplicateQuestion: (questionId: string) => void;
  addQuestion: () => void;
  onSelectRecipientsClick: () => void;
  createForm: () => void;
  updateForm: (formId: string) => void;
  loading: boolean;
  formId?: string;
  disableSelectRecipients: boolean;
  uploadFileToFirebaseStorage: (file: File) => Promise<string | null>;
  isTemplate?: boolean;
}

export const FormEditor: React.FC<Props> = ({
  methods,
  questions,
  findQuestion,
  moveQuestion,
  handleRemoveQuestion,
  duplicateQuestion,
  addQuestion,
  onSelectRecipientsClick,
  createForm,
  updateForm,
  loading,
  formId,
  disableSelectRecipients,
  uploadFileToFirebaseStorage,
  isTemplate = false,
}) => {
  const classes = useOnboardingStyles();

  const onSave = () => {
    methods.trigger();
    if (formId) {
      updateForm(formId);
    } else {
      createForm();
    }
  };

  return (
    <Box className={classes.mainContainer}>
      <Box className={classes.paper}>
        <Box className={classes.root} style={{ overflow: 'hidden', position: 'relative' }}>
          <FormProvider {...methods}>
            <form
              className={classes.form}
              style={{
                height: 'auto',
                gap: 16,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                overflow: 'auto',
                paddingBottom: 120,
              }}
            >
              <FormTitleStep />

              {questions.map((question, index) => (
                <FormQuestionStep
                  key={question.id || index}
                  questionIndex={index}
                  questionId={question.id}
                  findQuestion={findQuestion}
                  moveQuestion={moveQuestion}
                  handleRemove={() => handleRemoveQuestion(question.id)}
                  handleDuplicate={() => duplicateQuestion(question.id)}
                  uploadFileToFirebaseStorage={uploadFileToFirebaseStorage}
                />
              ))}

              <Box display="flex" justifyContent="flex-end">
                <Button
                  startIcon={<AiOutlinePlus />}
                  variant="contained"
                  color="primary"
                  onClick={addQuestion}
                >
                  Add a question
                </Button>
              </Box>
            </form>
          </FormProvider>

          <Paper variant="outlined" style={{ padding: 24, position: 'absolute', bottom: 0, width: '100%' }}>
            <Box display="flex" gridGap={16} justifyContent="space-between">
              <Button
                variant="outlined"
                style={{ width: 160 }}
                size="large"
                disabled={loading}
                color="primary"
                onClick={onSave}
              >
                <ButtonTitleWithLoading
                  title={isTemplate ? 'Save template' : 'Save as a draft'}
                  loaderVariant="primary"
                  loading={loading}
                />
              </Button>

              {!isTemplate && (
                <Button
                  variant="contained"
                  size="large"
                  color="primary"
                  onClick={onSelectRecipientsClick}
                  disabled={disableSelectRecipients}
                >
                  Select recipients
                </Button>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};
